import { useCallback, useEffect, useState } from "react";
import { APP_CONFIG } from "../constants/authConfig";
import {
  canChangeEmail,
  clearEmailSession,
  getRemainingEmailChanges,
  getStoredEmail,
  saveUserEmail,
} from "../services/authService";
import {
  checkSubscriptionStatus,
  openPaymentPage,
  registerUserInFirebase,
} from "../services/subscriptionService";

export function useAuth() {
  const [email, setEmail] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [remainingEmailChanges, setRemainingEmailChanges] = useState(APP_CONFIG.maxEmailChanges);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [error, setError] = useState(null);

  const refreshSubscription = useCallback(async (userEmail) => {
    const status = await checkSubscriptionStatus(userEmail);
    setHasAccess(status.hasAccess);
    setIsTrial(status.isTrial);
    setSubscriptionEndDate(status.endDate);
    return status;
  }, []);

  const refreshEmailChangeQuota = useCallback(async () => {
    const remaining = await getRemainingEmailChanges();
    setRemainingEmailChanges(remaining);
    return remaining;
  }, []);

  const bootstrapSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const storedEmail = await getStoredEmail();
      await refreshEmailChangeQuota();

      if (!storedEmail) {
        setIsAuthenticated(false);
        return;
      }

      await registerUserInFirebase(storedEmail);
      await refreshSubscription(storedEmail);
      setEmail(storedEmail);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err?.message || "Unable to restore your session.");
      await clearEmailSession();
      setEmail(null);
      setIsAuthenticated(false);
      setHasAccess(false);
      setIsTrial(false);
      setSubscriptionEndDate(null);
    } finally {
      setIsLoading(false);
    }
  }, [refreshEmailChangeQuota, refreshSubscription]);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  const signIn = useCallback(
    async (inputEmail, { isEmailChange = false } = {}) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const userEmail = await saveUserEmail(inputEmail, { isEmailChange });
        await registerUserInFirebase(userEmail);
        await refreshSubscription(userEmail);
        await refreshEmailChangeQuota();
        setEmail(userEmail);
        setIsAuthenticated(true);
        setIsChangingEmail(false);
      } catch (err) {
        if (!isEmailChange) {
          await clearEmailSession();
          setEmail(null);
          setIsAuthenticated(false);
          setHasAccess(false);
          setIsTrial(false);
          setSubscriptionEndDate(null);
        }
        setError(err?.message || "Unable to continue with this email.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [refreshEmailChangeQuota, refreshSubscription]
  );

  const requestEmailChange = useCallback(async () => {
    setError(null);
    const change = await canChangeEmail();

    if (!change.allowed) {
      setError(
        `You have used all ${APP_CONFIG.maxEmailChanges} email changes. Contact ${APP_CONFIG.supportEmail} for help.`
      );
      return false;
    }

    await clearEmailSession();
    setEmail(null);
    setIsAuthenticated(false);
    setHasAccess(false);
    setIsTrial(false);
    setSubscriptionEndDate(null);
    setIsChangingEmail(true);
    return true;
  }, []);

  const subscribe = useCallback(() => {
    openPaymentPage();
  }, []);

  return {
    email,
    isAuthenticated,
    hasAccess,
    isTrial,
    isSubscribed: hasAccess && !isTrial,
    subscriptionEndDate,
    remainingEmailChanges,
    isLoading,
    isSubmitting,
    isChangingEmail,
    error,
    signIn,
    requestEmailChange,
    subscribe,
    refreshSubscription,
  };
}
